import React, { useMemo } from 'react'
import {
  buildMonthCells,
  formatEventTimeRange,
  getEventsForDate,
  getWeekdayHeaders,
  isSameDay,
  MONTH_MAX_VISIBLE_EVENTS,
} from './dateUtils'
import { AvailabilityCalendarEvent, AvailabilityCalendarLabels, AvailabilitySchedule } from './types'
import {
  MonthDay,
  MonthDayHeader,
  MonthDays,
  MonthEvent,
  MonthHeaderRow,
  MoreEventsButton,
  DayNumberButton,
  ViewPanel,
} from './style'

interface MonthViewProps {
  date: Date
  events: AvailabilityCalendarEvent[]
  schedule: AvailabilitySchedule
  labels: AvailabilityCalendarLabels
  onSelectDay: (day: Date) => void
  onShowMore: (day: Date) => void
}

export const MonthView = ({
  date,
  events,
  schedule,
  labels,
  onSelectDay,
  onShowMore,
}: MonthViewProps): JSX.Element => {
  const cells = useMemo(
    () => buildMonthCells(date, schedule.workingDays),
    [date, schedule.workingDays]
  )
  const weekdayHeaders = useMemo(
    () => getWeekdayHeaders(schedule.workingDays),
    [schedule.workingDays]
  )
  const dayCount = Math.max(weekdayHeaders.length, 1)
  const today = useMemo(() => new Date(), [])

  return (
    <ViewPanel>
      <MonthHeaderRow $dayCount={dayCount}>
        {weekdayHeaders.map(name => (
          <div key={name}>{name}</div>
        ))}
      </MonthHeaderRow>

      <MonthDays $dayCount={dayCount}>
        {cells.map(cell => {
          const dayEvents = getEventsForDate(events, cell.date)
          const visible = dayEvents.slice(0, MONTH_MAX_VISIBLE_EVENTS)
          const hiddenCount = dayEvents.length - visible.length

          return (
            <MonthDay
              key={cell.date.toISOString()}
              $otherMonth={!cell.inCurrentMonth}
              $dayCount={dayCount}
            >
              <MonthDayHeader>
                <DayNumberButton
                  type="button"
                  $size="month"
                  $today={isSameDay(cell.date, today)}
                  $muted={!cell.inCurrentMonth && !isSameDay(cell.date, today)}
                  onClick={() => onSelectDay(cell.date)}
                >
                  {cell.date.getDate()}
                </DayNumberButton>
              </MonthDayHeader>

              {visible.map(event => {
                const timeRange = formatEventTimeRange(event)
                return (
                  <MonthEvent
                    key={event.id}
                    type="button"
                    $color={event.color}
                    title={`${timeRange} · ${event.title} · ${event.roomName}`}
                  >
                    {timeRange} {event.title}
                  </MonthEvent>
                )
              })}

              {hiddenCount > 0 && (
                <MoreEventsButton
                  type="button"
                  onClick={() => onShowMore(cell.date)}
                >
                  {labels.moreEvents(hiddenCount)}
                </MoreEventsButton>
              )}
            </MonthDay>
          )
        })}
      </MonthDays>
    </ViewPanel>
  )
}
